/**
 * Settings → MCPs (web.md §4; connector.md "Authentication", "Access levels", "Quota"): the user's
 * assistant connections. Each has a name, an access level, an Enabled switch (in the editor header,
 * fix 7) and the prototype's capability grid; the editor shows the MCP endpoint, the token and secret URL
 * ONCE after create / rotate with copy buttons and client snippets, and today's requests against the
 * limit. On a plan without connectors (or signed out) an upsell panel replaces the list.
 *
 * Records live on the server (`/api/connectors`); their display rows also reach the device through sync.
 */

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type Connector, type ConnectorAccess, type ConnectorSecretsResponse, type Engine, type MutationResult } from '@digester/core';
import { openDialog } from '../../app/dialogs.js';
import { reportError } from '../../app/errors.js';
import { useServices } from '../../app/services.js';
import { showToast } from '../../app/Toast.js';
import type { WebApiCaller } from '../../platform/api.js';
import { Icon } from '../../icons/Icon.js';
import { Tool } from '../../ui/Tool.js';
import { Field, Section, Select } from '../../ui/form.js';
import { ProMark, copyText, formatDateTime, useAccountState } from '../../account/shared.js';
import { qk } from '../../engine-client/keys.js';
import { EditorEmpty, EditorFrame, useDraft, type ItemsResult, type SettingsCategory, type SettingsEditorProps, type SettingsItem } from '../shell/index.js';
import {
  ACCESS_OPTIONS, accessLabel, capabilities, clientSnippets, forgetSecrets, revealSecrets, useConnectors, useRefreshConnectors, useRevealedSecrets,
  type RevealedSecrets,
} from './connectors.js';

function useItems(): ItemsResult {
  const { query } = useConnectors();
  const items: SettingsItem[] = (query?.data?.connectors ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    icon: c.icon || 'plug',
    subtitle: `${accessLabel(c.access)} · ${c.lastUsedAt ? `used ${formatDateTime(c.lastUsedAt)}` : 'not used yet'}`,
    enabled: c.enabled,
  }));
  return { items, isLoading: query?.isLoading ?? false, error: query?.error ?? null };
}

async function deleteConnector(api: WebApiCaller, id: string): Promise<void> {
  await api.call('connectorsDelete', { params: { id } });
  forgetSecrets(id);
}

function UpsellPanel({ signedIn }: { signedIn: boolean }) {
  const navigate = useNavigate();
  return (
    <EditorFrame title="Connect your assistant" icon="plug">
      <Section title="Assistant">
        <div className="upsell-panel" data-upsell="mcps">
          <div>
            <span className="plan-emblem">
              <Icon name="plug" />
            </span>
            <div>
              <h2>
                Your assistant, your messages <ProMark />
              </h2>
            </div>
          </div>
          <p>
            With Pro, ChatGPT, Claude or any MCP client can read your messages, write briefings and keep your folders in order — in your own
            assistant session, on your own subscription.
          </p>
          <div className="panel-actions">
            <button type="button" className="primary-button" onClick={() => navigate('/subscribe')}>
              More
            </button>
            {signedIn ? null : (
              <button type="button" className="secondary-button" onClick={() => navigate('/signin?next=/settings/mcps')}>
                Sign in
              </button>
            )}
          </div>
        </div>
      </Section>
    </EditorFrame>
  );
}

interface ConnectorForm {
  name: string;
  access: ConnectorAccess;
  enabled: boolean;
}

function CopyField({ label, value, name }: { label: string; value: string; name: string }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="field copy-field">
      <span>{label}</span>
      <span className="copy-row">
        <input ref={input} name={name} aria-label={label} readOnly value={value} onFocus={(e) => e.target.select()} />
        <button type="button" className="secondary-button" data-copy={name} onClick={() => void copyText(value, label, input.current)}>
          <Icon name="copy" /> Copy
        </button>
      </span>
    </div>
  );
}

function SecretsBlock({ connectorId, endpoint, secrets }: { connectorId: string; endpoint: string; secrets: RevealedSecrets }) {
  return (
    <div className="secret-block" data-secrets>
      <p className="field-help">Copy the token or the secret URL now. They are shown only once; after you leave or reload, only the last four characters remain.</p>
      <CopyField label="Token" name="token" value={secrets.token} />
      <CopyField label="Secret URL" name="secretUrl" value={secrets.secretUrl} />
      {clientSnippets(endpoint, secrets).map((s) => (
        <div className="snippet" key={s.id} data-snippet={s.id}>
          <div className="snippet-head">
            <span>{s.title}</span>
            <Tool icon="copy" label={`Copy ${s.title}`} onClick={() => void copyText(s.text, 'Snippet')} />
          </div>
          <pre>{s.text}</pre>
        </div>
      ))}
      <div className="panel-actions tight">
        <button type="button" className="secondary-button" onClick={() => forgetSecrets(connectorId)}>
          <Icon name="check" /> I have copied it
        </button>
      </div>
    </div>
  );
}

function ConnectorEditor({ connector, endpoint, usage }: { connector: Connector | null; endpoint: string; usage: { used: number; max: number; requestsToday: number; requestsPerDay: number } | null }) {
  const { api } = useServices();
  const navigate = useNavigate();
  const refresh = useRefreshConnectors();
  const secrets = useRevealedSecrets(connector?.id ?? null);
  const [busy, setBusy] = useState(false);
  const draft = useDraft<ConnectorForm>({
    initial: connector ? { name: connector.name, access: connector.access, enabled: connector.enabled } : { name: 'New connection', access: 'read', enabled: true },
    isNew: connector === null,
    savedMessage: connector ? 'Changes saved' : 'Connection created',
    onSave: async (value) => {
      if (!connector) {
        const created = await api.call<ConnectorSecretsResponse>('connectorsCreate', { body: { name: value.name.trim(), access: value.access } });
        revealSecrets(created.connector.id, { token: created.token, secretUrl: created.secretUrl });
        if (!value.enabled) await api.call('connectorsUpdate', { params: { id: created.connector.id }, body: { enabled: false } });
        await refresh();
        return created.connector.id;
      }
      await api.call('connectorsUpdate', { params: { id: connector.id }, body: { name: value.name.trim(), access: value.access, enabled: value.enabled } });
      await refresh();
      return undefined;
    },
  });
  const v = draft.value;

  const rotate = (): void => {
    if (!connector) return;
    openDialog('confirm', {
      title: 'Rotate token',
      message: `Make a new token for ${connector.name}?`,
      note: 'The current token and secret URL stop working at once. Update your assistant with the new ones.',
      confirmLabel: 'Rotate token',
      onConfirm: async () => {
        setBusy(true);
        try {
          const res = await api.call<ConnectorSecretsResponse>('connectorsRotate', { params: { id: connector.id } });
          revealSecrets(connector.id, { token: res.token, secretUrl: res.secretUrl });
          await refresh();
          showToast('New token created');
        } catch (error) {
          reportError(error);
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const remove = (): void => {
    if (!connector) return;
    openDialog('confirm', {
      title: 'Delete connection',
      message: `Delete ${connector.name}?`,
      note: 'Its token stops working at once. Messages, briefings and history stay as they are.',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          await deleteConnector(api, connector.id);
          draft.discard();
          await refresh();
          showToast('Connection deleted');
          navigate('/settings/mcps');
        } catch (error) {
          reportError(error);
        }
      },
    });
  };

  return (
    <EditorFrame title={v.name || 'New connection'} icon={connector?.icon || 'plug'} enabled={{ checked: v.enabled, onChange: (enabled) => draft.set((d) => ({ ...d, enabled })) }}>
      <Section title="Connection">
        <Field label="Name" name="name" value={v.name} required maxLength={80} onChange={(name) => draft.set((d) => ({ ...d, name }))} />
        <CopyField label="Endpoint" name="endpoint" value={endpoint} />
        <Select<ConnectorAccess> label="Access" name="access" value={v.access} options={ACCESS_OPTIONS} onChange={(access) => draft.set((d) => ({ ...d, access }))} />
      </Section>
      <Section title="Capabilities">
        <div className="capability-grid">
          {capabilities(v.access).map((c) => (
            <span key={c.label} className={c.on ? undefined : 'capability-off'}>
              <Icon name={c.on ? 'check' : 'close'} />
              {c.label}
            </span>
          ))}
        </div>
        <p className="field-help">The assistant runs in your own ChatGPT, Claude or other MCP client, on your subscription. It acts only within this access level.</p>
      </Section>
      {connector ? (
        <Section title="Token">
          {secrets ? (
            <SecretsBlock connectorId={connector.id} endpoint={endpoint} secrets={secrets} />
          ) : (
            <>
              <div className="read-only-row" data-token-hint>
                <span>Token</span>
                <strong>dgc_…{connector.tokenHint}</strong>
              </div>
              <div className="read-only-row">
                <span>Created</span>
                <strong>{formatDateTime(connector.createdAt)}</strong>
              </div>
              <div className="read-only-row">
                <span>Last used</span>
                <strong>{formatDateTime(connector.lastUsedAt)}</strong>
              </div>
            </>
          )}
          <div className="panel-actions">
            <button type="button" className="secondary-button" disabled={busy} onClick={rotate}>
              <Icon name="refresh-cw" /> Rotate token
            </button>
            <button type="button" className="secondary-button danger-button" disabled={busy} onClick={remove}>
              <Icon name="trash" /> Delete connection
            </button>
          </div>
        </Section>
      ) : (
        <Section title="Token">
          <p className="field-help">Save to create the connection. Its token and secret URL are shown once, right after.</p>
        </Section>
      )}
      {usage ? (
        <Section title="Usage">
          <div className="read-only-row">
            <span>Requests today · all connections</span>
            <strong data-requests>
              {usage.requestsToday} / {usage.requestsPerDay}
            </strong>
          </div>
          <div className="read-only-row">
            <span>Connections</span>
            <strong>
              {usage.used} / {usage.max}
            </strong>
          </div>
        </Section>
      ) : null}
    </EditorFrame>
  );
}

function McpEditor({ recordId }: SettingsEditorProps) {
  const { available, ready, query } = useConnectors();
  const account = useAccountState();
  if (!ready) return <EditorEmpty text="Loading…" />;
  if (!available || query?.error?.code === 'not_entitled') return <UpsellPanel signedIn={account.signedIn} />;
  const data = query?.data;
  if (!data) return <EditorEmpty text={query?.error ? query.error.message : 'Loading…'} />;
  const usage = { used: data.connectors.length, max: data.max, requestsToday: data.requestsToday, requestsPerDay: data.requestsPerDay };
  if (recordId === 'new') return <ConnectorEditor connector={null} endpoint={data.endpoint} usage={usage} />;
  const connector = data.connectors.find((c) => c.id === recordId) ?? null;
  if (!connector) return <EditorEmpty text={data.connectors.length ? 'Select an item' : 'No connections yet · New connection adds one'} />;
  return <ConnectorEditor connector={connector} endpoint={data.endpoint} usage={usage} />;
}

export const category: SettingsCategory = {
  id: 'mcps',
  name: 'MCPs',
  icon: 'plug',
  useItems,
  Editor: McpEditor,
  newLabel: 'New connection',
  async archive(_engine: Engine, item: SettingsItem, { services, queryClient }): Promise<MutationResult> {
    await deleteConnector(services.api, item.id);
    await queryClient.invalidateQueries({ queryKey: qk.connectors() });
    return { changed: 1, undoId: null };
  },
  archiveNote: 'Its token stops working at once. Messages, briefings and history stay as they are.',
};
